import { useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook to play a short beep sound using the Web Audio API.
 * @param {Object} options - Configuration for the beep.
 * @param {number} options.frequency - Beep frequency in Hz (default 800).
 * @param {number} options.duration - Beep duration in ms (default 300).
 * @param {number} options.volume - Volume level (0 to 1, default 0.3).
 * @returns {Object} { playBeep } - Function to trigger the beep.
 */
function useBeep({ frequency = 800, duration = 300, volume = 0.3 } = {}) {
  const audioContextRef = useRef(null);
  const timeoutRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // Get or create AudioContext (lazy initialization)
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
    }
    return audioContextRef.current;
  }, []);

  /**
   * Plays the beep.
   * @param {Object} callbacks - Optional callbacks for beep events.
   * @param {Function} callbacks.onStart - Called when the beep starts playing.
   * @param {Function} callbacks.onComplete - Called when the beep finishes.
   * @param {Function} callbacks.onError - Called if an error occurs.
   */
  const playBeep = useCallback(({ onStart, onComplete, onError } = {}) => {
    const audioContext = getAudioContext();

    // Helper to actually create and play the beep
    const play = () => {
      try {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + duration / 1000
        );

        // Clean up nodes after playback
        oscillator.onended = () => {
          oscillator.disconnect();
          gainNode.disconnect();
        };

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration / 1000);

        // Notify start
        if (onStart) onStart();

        // Schedule completion notification (fallback in case onended is unreliable)
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          if (onComplete) onComplete();
        }, duration);
      } catch (error) {
        console.error('Error playing beep:', error);
        if (onError) onError(error);
        // Fallback completion
        timeoutRef.current = setTimeout(() => {
          if (onComplete) onComplete();
        }, duration);
      }
    };

    // Resume audio context if suspended (required on iOS and some browsers)
    if (audioContext.state === 'suspended') {
      audioContext.resume()
        .then(play)
        .catch((err) => {
          console.error('Failed to resume audio context:', err);
          play(); // attempt to play anyway
        });
    } else {
      play();
    }
  }, [frequency, duration, volume, getAudioContext]);

  return { playBeep };
}

export default useBeep;